/**
 * Step 06 — Education History
 *
 * KEPT from old step-07 schema. Same structure: highest qualification,
 * Class 10 & 12 details, degree repeaters, certificates, notes.
 *
 * @module udp-schema/step-06
 */

import { z } from 'zod';
import { HIGHEST_QUALIFICATION_VALUES } from './shared-enums.js';
import { optionalYearSchema } from './zod-helpers.js';

// ─── Reusable sub-schemas ────────────────────────────────────────────────────

export const degreeEntrySchema = z.object({
  degreeName: z.string().trim().optional(),
  institutionName: z.string().trim().optional(),
  university: z.string().trim().optional(),
  yearOfPassing: optionalYearSchema,
  courseOrStream: z.string().trim().optional(),
  marksOrGrade: z.string().trim().optional(),
  isCurrentlyStudying: z.boolean().optional(),
});

export const certificateEntrySchema = z.object({
  certificateName: z.string().trim().optional(),
  institutionName: z.string().trim().optional(),
  yearOfCompletion: optionalYearSchema,
  fieldOrSubject: z.string().trim().optional(),
});

// ─── Main Step 06 Schema ────────────────────────────────────────────────────

export const step06EducationSchema = z.object({
  /** Highest level of formal education completed or in progress —
   *  canonical 8-bucket enum from `shared-enums`. */
  highestQualification: z.enum(HIGHEST_QUALIFICATION_VALUES).optional(),

  /**
   * Class 10 / Secondary school details.
   * `board` is a string value from boardOptions (cbse / icse / state_board / ...).
   * `subjects` is a multi-select array of subject keys from subjectOptions.
   */
  education10th: z
    .object({
      board: z.string().trim().optional(),
      schoolName: z.string().trim().optional(),
      yearOfPassing: optionalYearSchema,
      subjects: z.array(z.string().trim()).optional(),
      marksObtained: z.string().trim().optional(),
    })
    .optional(),

  /**
   * Class 12 / Higher Secondary details.
   * `boardOrUniversity` is a string value from boardOptions.
   * `courseOrStream` is a string value from streamOptions.
   * `subjects` is a multi-select array of subject keys.
   */
  education12th: z
    .object({
      institutionName: z.string().trim().optional(),
      boardOrUniversity: z.string().trim().optional(),
      yearOfPassing: optionalYearSchema,
      courseOrStream: z.string().trim().optional(),
      subjects: z.array(z.string().trim()).optional(),
      marksOrGrade: z.string().trim().optional(),
    })
    .optional(),

  /** List of Diploma qualifications (one or more) */
  diplomas: z.array(degreeEntrySchema).optional(),

  /** List of Graduation / Bachelor's degree qualifications */
  graduations: z.array(degreeEntrySchema).optional(),

  /** List of Post Graduation / Master's degree qualifications */
  postGraduations: z.array(degreeEntrySchema).optional(),

  /** List of Doctorate / PhD qualifications */
  doctorates: z.array(degreeEntrySchema).optional(),

  /** List of Post Doctoral research or fellowships */
  postDoctorates: z.array(degreeEntrySchema).optional(),

  /** Other certificates, short courses, or professional qualifications */
  otherCertificates: z.array(certificateEntrySchema).optional(),
});

// ─── Inferred types ──────────────────────────────────────────────────────────

export type DegreeEntryInput = z.infer<typeof degreeEntrySchema>;
export type CertificateEntryInput = z.infer<typeof certificateEntrySchema>;
export type Step06EducationInput = z.infer<typeof step06EducationSchema>;
